import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Loader2, CheckCircle, AlertCircle, XCircle, Plus } from "lucide-react";
import api, { formatApiError } from "../api";
import { Button, Card, Badge } from "./ui/Primitives";
import { formatEUR } from "../utils/format";
import UnifiedImportButton from "./UnifiedImportButton";

/**
 * Phase 30 F8 — Menu Engineering Live-Check via PDF upload.
 * Phase 31 — Bulk-Add: "Alle fehlenden hinzufügen" button creates all red
 * items as draft recipes/cocktails with F&B Intelligence category auto-detection.
 */
export default function MenuPdfCheck({ onItemAdded, onBulkAdded }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [activeKind, setActiveKind] = useState(null);
  // (Refs for hidden file inputs removed in Phase 32 — handled by UnifiedImportButton.)

  const upload = async (kind, file) => {
    if (!file) return;
    setBusy(true);
    setActiveKind(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/engineering/menu-check/${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setResult(null); setActiveKind(null); };

  // Phase 31 — Bulk add all red (missing) items as drafts
  const bulkAddMissing = async () => {
    if (!result) return;
    const reds = result.items.filter((it) => it.status === "red");
    if (reds.length === 0) {
      toast.error(t("engineering_pdf.no_missing", "Keine fehlenden Posten zum Hinzufügen."));
      return;
    }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/engineering/menu-check-bulk-add", {
        kind: result.kind,
        items: reds.map((r) => ({ name: r.extracted_name, selling_price: r.extracted_price })),
      });
      toast.success(data.message_de || `${data.created} Gerichte hinzugefügt.`);
      onBulkAdded && onBulkAdded(result.kind, data.created);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBulkBusy(false);
    }
  };

  const Icon = ({ status }) => {
    if (status === "green") return <CheckCircle size={14} className="text-ok" />;
    if (status === "yellow") return <AlertCircle size={14} className="text-warn" />;
    return <XCircle size={14} className="text-err" />;
  };

  return (
    <Card className="p-5" data-testid="menu-pdf-check">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-brand mb-1">
            {t("engineering_pdf.kicker", "Live-Check")}
          </div>
          <h3 className="text-base font-semibold text-ink-primary">
            {t("engineering_pdf.title", "Karte gegen Datenbank prüfen")}
          </h3>
          <p className="text-sm text-ink-secondary mt-1 max-w-xl">
            {t("engineering_pdf.subtitle",
              "PDF Ihrer Karte hochladen — F&B Intelligence prüft alle Gerichte/Drinks gegen Ihre Datenbank und zeigt fehlende Posten und kritische Food/Pour-Cost-Werte.")}
          </p>
        </div>
        {result && (
          <Button variant="ghost" size="sm" onClick={reset} data-testid="pdf-check-reset">
            {t("common.cancel", "Schließen")}
          </Button>
        )}
      </div>

      {!result && (
        <div className="flex flex-wrap items-center gap-3">
          <UnifiedImportButton
            acceptCsvXlsx={false}
            acceptPdf
            onPdfFile={(f) => upload("kitchen", f)}
            label={t("engineering_pdf.check_food_menu", "Speisekarte prüfen")}
            testId="pdf-check-kitchen-btn"
            variant="secondary"
          />
          <UnifiedImportButton
            acceptCsvXlsx={false}
            acceptPdf
            onPdfFile={(f) => upload("bar", f)}
            label={t("engineering_pdf.check_bar_menu", "Getränkekarte prüfen")}
            testId="pdf-check-bar-btn"
            variant="secondary"
          />
          {busy && (
            <span className="text-[11px] text-ink-muted self-center flex items-center gap-1">
              <Loader2 size={11} className="animate-spin" /> {t("common.loading", "wird verarbeitet …")} ({activeKind})
            </span>
          )}
        </div>
      )}

      {result && (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-3" data-testid="pdf-check-summary">
            <Card className="p-3">
              <div className="text-[10px] uppercase tracking-wider text-ink-muted">Total</div>
              <div className="num text-2xl font-medium">{result.total}</div>
              <div className="text-[10px] text-ink-muted">{result.filename}</div>
            </Card>
            <Card className="p-3 border-ok/30">
              <div className="text-[10px] uppercase tracking-wider text-ink-muted flex items-center gap-1"><CheckCircle size={11} className="text-ok" /> OK</div>
              <div className="num text-2xl font-medium text-ok" data-testid="pdf-check-green">{result.summary.green}</div>
            </Card>
            <Card className="p-3 border-warn/30">
              <div className="text-[10px] uppercase tracking-wider text-ink-muted flex items-center gap-1"><AlertCircle size={11} className="text-warn" /> {t("engineering_pdf.cost_high", "Kosten hoch")}</div>
              <div className="num text-2xl font-medium text-warn" data-testid="pdf-check-yellow">{result.summary.yellow}</div>
            </Card>
            <Card className="p-3 border-err/30">
              <div className="text-[10px] uppercase tracking-wider text-ink-muted flex items-center gap-1"><XCircle size={11} className="text-err" /> {t("engineering_pdf.not_in_db", "Fehlt in DB")}</div>
              <div className="num text-2xl font-medium text-err" data-testid="pdf-check-red">{result.summary.red}</div>
            </Card>
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="text-sm text-ink-secondary" data-testid="pdf-check-headline">
              {t("engineering_pdf.check_headline",
                "{{found}} von {{total}} Gerichten kalkuliert — {{missing}} fehlen",
                {
                  found: result.summary.green + result.summary.yellow,
                  total: result.total,
                  missing: result.summary.red,
                })}
            </div>
            {result.summary.red > 0 && (
              <Button
                onClick={bulkAddMissing}
                disabled={bulkBusy}
                data-testid="pdf-check-bulk-add-btn"
              >
                {bulkBusy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                {t("engineering_pdf.bulk_add", "Alle fehlenden hinzufügen ({{n}})", { n: result.summary.red })}
              </Button>
            )}
          </div>

          <Card className="overflow-hidden">
            <div className="max-h-[55vh] overflow-y-auto">
              <table className="w-full text-sm" data-testid="pdf-check-table">
                <thead className="bg-white/[0.02] sticky top-0 z-10">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-ink-muted">
                    <th className="px-3 py-2 w-8"></th>
                    <th className="px-3 py-2">{t("engineering_pdf.col_extracted", "Aus PDF")}</th>
                    <th className="px-3 py-2 w-28 text-right">{t("imports.col_price")}</th>
                    <th className="px-3 py-2">{t("engineering_pdf.col_match", "Treffer")}</th>
                    <th className="px-3 py-2 w-24 text-right">Cost %</th>
                    <th className="px-3 py-2">{t("engineering_pdf.col_reason", "Status")}</th>
                    <th className="px-3 py-2 w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {result.items.map((it, i) => (
                    <tr key={i} className="border-t border-white/5" data-testid={`pdf-check-row-${i}`}>
                      <td className="px-3 py-2"><Icon status={it.status} /></td>
                      <td className="px-3 py-2 text-ink-primary">{it.extracted_name}</td>
                      <td className="px-3 py-2 text-right num">{it.extracted_price != null ? formatEUR(it.extracted_price, 2) : "—"}</td>
                      <td className="px-3 py-2 text-ink-secondary">
                        {it.match_name ? <span>{it.match_name} <Badge tone="default" className="ml-1">{it.fuzzy_score}%</Badge></span> : <span className="text-ink-muted">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right num">
                        {it.match_cost_pct != null ? `${it.match_cost_pct.toFixed(1).replace(".", ",")} %` : "—"}
                      </td>
                      <td className="px-3 py-2 text-[12px] text-ink-secondary">{it.reason}</td>
                      <td className="px-3 py-2 text-right">
                        {it.status === "red" && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => onItemAdded?.(result.kind, it.extracted_name, it.extracted_price)}
                            data-testid={`pdf-check-add-${i}`}
                            title={t("engineering_pdf.add_to_db", "Zur Datenbank hinzufügen")}
                          >
                            <Plus size={11} /> DB
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}
    </Card>
  );
}
